import { useMutation } from '@apollo/client/react';
import { useCallback, useState } from 'react';
import { useDispatch } from 'react-redux';
import { assertIsDefined } from '../../../../common/asserts/assert';
import type { BasicBooking } from '../../../../common/graphql/fragments/gql/BasicBooking';
import { switchBookingMutation } from '../../../../common/graphql/switchBookingQuery';
import { Errors } from '../../../../common/texts';
import { trackEvent } from '../../../../common/tracking/trackerService';
import {
  type InformationMessage,
  InformationMessageType,
} from '../../../../common-ui/information-message/informationMessageUtils';
import { BookingFilterState } from '../../../../gql/graphql';
import { clearModals } from '../../../../services/modal/modalActions';
import {
  addBookingResultNotification,
  type BookingResult,
} from '../../../../services/notifications/notificationsActions';
import { showBookingResultToast } from '../../../../services/notifications/utils/showToast';
import { setTempSwitchResult } from '../../../../services/switch/switchActions';
import { initializeAllUpdateFormsWithBooking } from '../../../../services/update-form/updateFormActionsHelpers';
import {
  getSwitchSubmitInformation,
  prepareSubmitParams,
  SWITCH_SUBMIT_SHOW,
  type SwitchSubmitInformation,
} from '../utils/switchUtils';

const getSwitchBookingVariable = (booking1: BasicBooking, booking2: BasicBooking) =>
  booking1.status === BookingFilterState.Confirmed
    ? prepareSubmitParams(booking1, booking2)
    : prepareSubmitParams(booking2, booking1);

export const useSwitch = () => {
  const dispatch = useDispatch();

  const [statusInformationList, setStatusInformationList] = useState<InformationMessage[]>([]);

  const [submitSwitch, { loading }] = useMutation(switchBookingMutation);

  const switchBookings = useCallback(
    async (booking1: BasicBooking, booking2: BasicBooking | null) => {
      if (!booking2) {
        trackEvent({
          category: 'Switch',
          action: 'Error',
          label: 'Second booking not selected',
        });

        setStatusInformationList([
          {
            message: Errors.SelectSecondBooking,
            type: InformationMessageType.HardWarning,
          },
        ]);
        return;
      }

      trackEvent({
        category: 'Switch',
        action: 'Perform switch',
        label: `${booking1.status} --> ${booking2.status}`,
      });

      const variables = getSwitchBookingVariable(booking1, booking2);

      const { waitlistedBookingNo, confirmedBookingNo } = variables;

      try {
        const result = await submitSwitch({ variables });
        if (result?.data?.switchBookings) {
          setStatusInformationList([]);
          const switchSubmitInformation: SwitchSubmitInformation = getSwitchSubmitInformation(
            result.data.switchBookings,
          );

          if (switchSubmitInformation.status === SWITCH_SUBMIT_SHOW.SUCCESS) {
            const { confirmedBooking, waitlistedBooking } = result.data.switchBookings;

            assertIsDefined(confirmedBooking, 'Confirmed booking is not defined');
            assertIsDefined(waitlistedBooking, 'Waitlisted booking is not defined');

            initializeAllUpdateFormsWithBooking(confirmedBooking).forEach(dispatch);
            initializeAllUpdateFormsWithBooking(waitlistedBooking).forEach(dispatch);

            const bookingResult: BookingResult = {
              type: 'switch-success',
              confirmedBookingNo: confirmedBooking.bookingNo,
              waitlistedBookingNo: waitlistedBooking.bookingNo,
              sailing: confirmedBooking.sailing,
              messages: switchSubmitInformation.messages,
              warnings: switchSubmitInformation.warnings,
            };
            showBookingResultToast(bookingResult);
            dispatch(addBookingResultNotification(bookingResult));

            dispatch(
              setTempSwitchResult({
                confirmedBooking,
                waitlistedBooking,
              }),
            );

            dispatch(clearModals());
          } else {
            const errors = switchSubmitInformation.errors;

            trackEvent({ category: 'Switch', action: 'Error' });

            if (errors && errors.length > 0) {
              const bookingResult: BookingResult = {
                type: 'switch-fail',
                confirmedBookingNo,
                waitlistedBookingNo,
                errors,
              };
              showBookingResultToast(bookingResult);
              dispatch(addBookingResultNotification(bookingResult));

              setStatusInformationList(
                errors.map(message => ({
                  type: InformationMessageType.HardWarning,
                  message,
                })),
              );
            } else {
              const bookingResult: BookingResult = {
                type: 'switch-fail',
                confirmedBookingNo,
                waitlistedBookingNo,
                errors: [Errors.SomethingWentWrongTryAgain],
              };
              showBookingResultToast(bookingResult);
              dispatch(addBookingResultNotification(bookingResult));

              setStatusInformationList([
                {
                  message: Errors.SomethingWentWrongTryAgain,
                  type: InformationMessageType.HardWarning,
                },
              ]);
            }
          }
          return result.data.switchBookings;
        }
      } catch (_error) {
        trackEvent({ category: 'Switch', action: 'Error' });

        showBookingResultToast({
          type: 'switch-fail',
          confirmedBookingNo,
          waitlistedBookingNo,
          errors: [Errors.SomethingWentWrongTryAgain],
        });

        setStatusInformationList([
          {
            message: Errors.SomethingWentWrongTryAgain,
            type: InformationMessageType.HardWarning,
          },
        ]);
      }
    },
    [dispatch, submitSwitch],
  );

  return {
    loading,
    statusInformationList,
    switchBookings,
    setStatusInformationList,
  };
};
